import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, updateDoc, setDoc } from 'firebase/firestore';
import { initializeApp, deleteApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import './Auth.css';

// Firebase config (same project, secondary app so admin stays logged in)
const firebaseConfig = {
  apiKey: 'AIzaSyDjrTYwexV3RzYbtVEKJeuvLxAe3QNqOHw',
  authDomain: 'desibitesweb.firebaseapp.com',
  projectId: 'desibitesweb',
  storageBucket: 'desibitesweb.firebasestorage.app',
  messagingSenderId: '886525824200',
  appId: '1:886525824200:web:f8c1c018cd87733fe2a9fd',
};

const ROLES = [
  { key: 'customer', icon: '👤', label: 'Customer' },
  { key: 'employee', icon: '🍳', label: 'Employee' },
  { key: 'manager', icon: '📋', label: 'Manager' },
  { key: 'admin', icon: '🛡️', label: 'Admin' },
  { key: 'owner', icon: '👑', label: 'Owner' },
];

const AdminUsers = () => {
  const { user, role, loading } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [confirm, setConfirm] = useState(null); // { uid, name, newRole }

  // ── Create Account state ──
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newRole, setNewRole] = useState('employee');
  const [creating, setCreating] = useState(false);
  const [createMsg, setCreateMsg] = useState({ type: '', text: '' });
  const secondaryApp = useRef(null);

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setCreateMsg({ type: '', text: '' });
    if (!newName.trim() || !newEmail.trim() || !newPassword.trim()) {
      setCreateMsg({ type: 'error', text: 'Name, email, and password are required.' });
      return;
    }
    if (newPassword.length < 6) {
      setCreateMsg({ type: 'error', text: 'Password must be at least 6 characters.' });
      return;
    }
    setCreating(true);
    try {
      // Use a secondary Firebase app so the admin stays logged in
      secondaryApp.current = initializeApp(firebaseConfig, 'SecondaryApp');
      const secondaryAuth = getAuth(secondaryApp.current);
      const cred = await createUserWithEmailAndPassword(secondaryAuth, newEmail.trim(), newPassword);
      await updateProfile(cred.user, { displayName: newName.trim() });
      // Save user doc to Firestore
      await setDoc(doc(db, 'users', cred.user.uid), {
        name: newName.trim(),
        email: newEmail.trim(),
        phone: newPhone.trim() || '',
        role: newRole,
        createdAt: new Date().toISOString(),
        createdBy: user.uid,
      });
      // Sign out from secondary app & clean up
      await secondaryAuth.signOut();
      await deleteApp(secondaryApp.current);
      secondaryApp.current = null;
      setCreateMsg({ type: 'success', text: `✅ Account created for ${newName.trim()} (${newEmail.trim()})` });
      setNewName(''); setNewEmail(''); setNewPassword(''); setNewPhone(''); setNewRole('employee');
      fetchUsers(); // refresh the list
    } catch (err) {
      console.error('Create account error:', err);
      let msg = 'Failed to create account.';
      if (err.code === 'auth/email-already-in-use') msg = 'This email is already registered.';
      else if (err.code === 'auth/invalid-email') msg = 'Invalid email address.';
      else if (err.code === 'auth/weak-password') msg = 'Password is too weak.';
      setCreateMsg({ type: 'error', text: msg });
      if (secondaryApp.current) { try { await deleteApp(secondaryApp.current); } catch {} secondaryApp.current = null; }
    }
    setCreating(false);
  };

  useEffect(() => {
    if (loading) return;
    if (!user || !['owner', 'admin'].includes(role)) { navigate('/'); return; }
    fetchUsers();
    // eslint-disable-next-line
  }, [user, role, loading]);

  const fetchUsers = async () => {
    setLoadingUsers(true);
    setError('');
    try {
      const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 8000));
      const snap = await Promise.race([getDocs(collection(db, 'users')), timeout]);
      const list = [];
      snap.forEach((d) => list.push({ uid: d.id, ...d.data() }));
      list.sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''));
      setUsers(list);
    } catch (err) {
      console.error('Failed to load users:', err);
      setError('Could not load users. Make sure Firestore is enabled in Firebase Console.');
    }
    setLoadingUsers(false);
  };

  const handleRoleClick = (uid, name, newRole) => {
    setConfirm({ uid, name, newRole });
  };

  const confirmRoleChange = async () => {
    if (!confirm) return;
    const { uid, newRole } = confirm;
    setSaving(uid);
    setConfirm(null);
    try {
      const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 5000));
      await Promise.race([updateDoc(doc(db, 'users', uid), { role: newRole }), timeout]);
      setUsers((prev) => prev.map((u) => u.uid === uid ? { ...u, role: newRole } : u));
    } catch (err) {
      console.error('Failed to update role:', err);
      alert('Failed to update role. Check Firestore.');
    }
    setSaving(null);
  };

  if (loading || !user) return null;
  if (!['owner', 'admin'].includes(role)) return null;

  const ROLE_COLORS = { owner: '#f59e0b', admin: '#a855f7', manager: '#10b981', employee: '#3b82f6', customer: 'rgba(255,255,255,0.4)' };

  // Filter and search
  const filtered = users.filter((u) => {
    if (filterRole !== 'all' && u.role !== filterRole) return false;
    if (search) {
      const q = search.toLowerCase();
      return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q) || (u.phone || '').includes(q);
    }
    return true;
  });

  return (
    <div className="auth" style={{ alignItems: 'flex-start', paddingTop: 40 }}>
      <div className="admin" style={{ width: '100%', maxWidth: 700, position: 'relative' }}>
        <div className="auth__logo"><span>Desi Bites</span></div>
        <h1 className="auth__title">User Management</h1>
        <p className="auth__sub">{users.length} registered user{users.length !== 1 ? 's' : ''}</p>

        {/* Search & Filter */}
        <div className="admin__toolbar">
          <input
            className="auth__input admin__search"
            type="text"
            placeholder="🔍 Search by name, email, or phone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="admin__filters">
            {[{ key: 'all', label: 'All' }, ...ROLES].map((r) => (
              <button
                key={r.key}
                className={`admin__filter-btn ${filterRole === r.key ? 'admin__filter-btn--active' : ''}`}
                onClick={() => setFilterRole(r.key)}
              >
                {r.icon && <span>{r.icon} </span>}{r.label}
                {r.key !== 'all' && <span className="admin__filter-count">{users.filter((u) => u.role === r.key).length}</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Create Account Section */}
        <div className="admin__create">
          <button
            className={`admin__create-toggle ${showCreate ? 'admin__create-toggle--open' : ''}`}
            onClick={() => { setShowCreate(!showCreate); setCreateMsg({ type: '', text: '' }); }}
          >
            <span className="admin__create-toggle-icon">{showCreate ? '−' : '+'}</span>
            <span>Create Employee / Staff Account</span>
          </button>
          {showCreate && (
            <form className="admin__create-form" onSubmit={handleCreateAccount}>
              <div className="admin__create-row">
                <div className="auth__field">
                  <label className="auth__label">Full Name *</label>
                  <input className="auth__input" type="text" placeholder="John Doe" value={newName} onChange={(e) => setNewName(e.target.value)} />
                </div>
                <div className="auth__field">
                  <label className="auth__label">Email *</label>
                  <input className="auth__input" type="email" placeholder="employee@email.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
                </div>
              </div>
              <div className="admin__create-row">
                <div className="auth__field">
                  <label className="auth__label">Temp Password *</label>
                  <input className="auth__input" type="text" placeholder="Min 6 characters" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
                </div>
                <div className="auth__field">
                  <label className="auth__label">Phone (optional)</label>
                  <input className="auth__input" type="tel" placeholder="(xxx) xxx-xxxx" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} />
                </div>
              </div>
              <div className="auth__field">
                <label className="auth__label">Role</label>
                <div className="auth__roles">
                  {ROLES.map((r) => (
                    <div
                      key={r.key}
                      className={`auth__role ${newRole === r.key ? 'auth__role--active' : ''}`}
                      onClick={() => setNewRole(r.key)}
                    >
                      <span className="auth__role-icon">{r.icon}</span>
                      <span className="auth__role-label">{r.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              {createMsg.text && (
                <div className={createMsg.type === 'success' ? 'acct__success' : 'auth__error'}>
                  {createMsg.text}
                </div>
              )}
              <button className="auth__btn" type="submit" disabled={creating} style={{ alignSelf: 'flex-start' }}>
                {creating ? 'Creating…' : '🔑 Create Account'}
              </button>
            </form>
          )}
        </div>

        {error && <div className="auth__error" style={{ marginBottom: 16 }}>{error}</div>}

        {/* Confirmation Modal */}
        {confirm && (
          <div className="admin__modal-overlay" onClick={() => setConfirm(null)}>
            <div className="admin__modal" onClick={(e) => e.stopPropagation()}>
              <div className="admin__modal-icon">{ROLES.find((r) => r.key === confirm.newRole)?.icon}</div>
              <h3 className="admin__modal-title">Change Role?</h3>
              <p className="admin__modal-desc">
                Change <strong>{confirm.name}</strong>'s role to <strong style={{ color: ROLE_COLORS[confirm.newRole] }}>{confirm.newRole}</strong>?
              </p>
              <div className="admin__modal-actions">
                <button className="admin__modal-cancel" onClick={() => setConfirm(null)}>Cancel</button>
                <button className="auth__btn" style={{ fontSize: 13, padding: '10px 24px' }} onClick={confirmRoleChange}>Confirm</button>
              </div>
            </div>
          </div>
        )}

        {loadingUsers ? (
          <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.4)', padding: 40 }}>Loading users…</p>
        ) : filtered.length === 0 ? (
          <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.4)', padding: 40 }}>
            {users.length === 0 ? 'No users found.' : 'No users match your search.'}
          </p>
        ) : (
          <div className="admin__list">
            {filtered.map((u) => (
              <div className="admin__user" key={u.uid}>
                <div className="admin__user-info">
                  <div className="admin__user-name">{u.name || '(no name)'}</div>
                  <div className="admin__user-email">{u.email}</div>
                  {u.phone && <div className="admin__user-phone">📞 {u.phone}</div>}
                  <div className="admin__user-uid">UID: {u.uid}</div>
                </div>
                <div className="admin__user-actions">
                  <div className="admin__role-btns">
                    {ROLES.map((r) => (
                      <button
                        key={r.key}
                        className={`admin__role-btn ${u.role === r.key ? 'admin__role-btn--active' : ''}`}
                        style={u.role === r.key ? { borderColor: ROLE_COLORS[r.key], color: ROLE_COLORS[r.key] } : {}}
                        onClick={() => u.role !== r.key && handleRoleClick(u.uid, u.name || u.email, r.key)}
                        disabled={saving === u.uid || u.uid === user.uid || u.role === r.key}
                        title={u.uid === user.uid ? "Can't change your own role" : u.role === r.key ? 'Current role' : `Set as ${r.label}`}
                      >
                        <span>{r.icon}</span>
                        <span>{r.label}</span>
                      </button>
                    ))}
                  </div>
                  {saving === u.uid && <span className="admin__saving">Saving…</span>}
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <button className="auth__btn" onClick={fetchUsers} style={{ fontSize: 12, padding: '10px 24px' }}>
            ↻ Refresh
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;
